import React, { useEffect, useState } from 'react';
import { Mail, ExternalLink } from 'lucide-react';
import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { getAuthHeaders, getApiBase } from '../../utils/api';

export function sourceDocuments(component = {}) {
  const original = component.originalComponent || component;
  const files = [...(original.component_data?.attached_files || []), ...(component.component_data?.attached_files || [])];
  const links = new Map();
  for (const file of files) {
    const id = file.documentId || file.id;
    const email = file.source === 'inbound_email_body';
    const name = file.name || file.filename || '';
    if (!email && /\.json$/i.test(name)) continue;
    const logId = file.parsingLogId || file.parsing_log_id;
    const path = email && logId ? `/parsing-logs/${encodeURIComponent(logId)}/email`
      : id && !String(id).startsWith('temp-') && id !== 'undefined' ? `/files/${encodeURIComponent(id)}/original` : null;
    if (path) links.set(path, { path, label: email ? 'Original email' : /\.pdf$/i.test(name) ? 'Original PDF' : 'Original document', name: email ? 'Original email' : name, type: email ? 'Email' : /\.pdf$/i.test(name) ? 'PDF' : 'Document', source: file.sender || (email ? 'Email' : name || 'Uploaded document'), date: file.received_at || file.uploaded_at || file.created_at || file.uploadedAt, metadataPath: `/source-documents/${email && logId ? 'email' : 'file'}/${encodeURIComponent(email && logId ? logId : id)}` });
  }
  if (!links.size) {
    const logId = original.parsing_log_id;
    const id = original.document_id || original.component_data?.document_id;
    const path = id ? `/files/${encodeURIComponent(id)}/original` : logId ? `/parsing-logs/${encodeURIComponent(logId)}/email` : null;
    if (path) links.set(path, { path, label: !id && logId ? 'Original email' : 'Original document', metadataPath: `/source-documents/${id ? 'file' : 'email'}/${encodeURIComponent(id || logId)}` });
  }
  return [...links.values()];
}

export function sortSourceDocuments(documents) {
  const timestamp = doc => { const time = Date.parse(doc.date || ''); return Number.isFinite(time) ? time : -Infinity; };
  return [...documents].sort((a, b) => timestamp(b) - timestamp(a));
}

export default function SourceDocumentLinks({ component }) {
  const documents = sourceDocuments(component);
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const signature = JSON.stringify(documents);
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setDetails(null);
    const sources = JSON.parse(signature);
    Promise.all(sources.map(async doc => {
      try {
        const response = await fetch(`${getApiBase()}${doc.metadataPath}`, { headers: getAuthHeaders(), credentials: 'include' });
        if (!response.ok) throw new Error('Metadata unavailable');
        const metadata = await response.json();
        return { ...doc, ...metadata };
      } catch { return { ...doc, unavailable: true }; }
    })).then(rows => { if (active) setDetails(sortSourceDocuments(rows)); });
    return () => { active = false; };
  }, [open, signature]);
  if (!documents.length) return null;
  const iconClass = 'inline-flex items-center justify-center h-8 w-8 rounded-md focus-visible:ring-2 focus-visible:ring-ui-blue-solid text-ui-link hover:bg-ui-hover';
  const icon = <Mail className="h-4 w-4" aria-hidden="true" />;
  if (documents.length === 1) return <a href={`${getApiBase()}${documents[0].path}`}
    target="_blank" rel="noopener noreferrer" aria-label="Open original source" title="Open original source"
    onClick={event => event.stopPropagation()} className={iconClass}>{icon}</a>;
  return <Dialog open={open} onOpenChange={setOpen}>
    <DialogTrigger asChild><button type="button" className={iconClass}
      aria-label="Choose original document" title="Original documents" onClick={event => event.stopPropagation()}>{icon}</button></DialogTrigger>
    <DialogContent onClick={event => event.stopPropagation()} className="inset-auto left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-md max-h-[75vh] rounded-lg p-5 pt-5 overflow-y-auto bg-ui-raised border-ui-line text-ui-ink">
      <DialogTitle>Original documents</DialogTitle>
      <DialogDescription className="text-ui-muted">Most recent first. Select a document to open.</DialogDescription>
      {!details ? <p role="status" className="text-sm">Loading documents…</p> : <ul className="divide-y divide-ui-divider">
        {details.map(doc => {
          const date = new Date(doc.date || '');
          const dateLabel = Number.isFinite(date.getTime()) ? date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'Date unavailable';
          return <li key={doc.path}><a href={`${getApiBase()}${doc.path}`} target="_blank" rel="noopener noreferrer"
            onClick={() => setOpen(false)} className="flex items-center justify-between gap-3 py-3 rounded hover:bg-ui-hover focus-visible:ring-2 focus-visible:ring-ui-blue-solid">
            <span className="min-w-0 text-sm"><span className="block font-medium">{dateLabel} · {doc.type || 'Document'}</span>
              <span className="block text-ui-muted break-words">{doc.source || doc.name || 'Source unavailable'}</span>
              {doc.unavailable && <span className="block text-xs text-ui-muted">Some details unavailable</span>}
            </span><ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
          </a></li>;
        })}
      </ul>}
    </DialogContent>
  </Dialog>;
}
